import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tag, Plus, PencilSimple, Trash, ArrowUp, ArrowDown, Eye, EyeSlash } from "@phosphor-icons/react";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";
const empty = { nama: "", deskripsi: "", urutan: 0, aktif: true, tampil_publik: true };

export default function MasterCategories() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const canManage = user?.role === "super_admin" || user?.role === "admin";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/categories").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, urutan: items.length }); setEditing("new"); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditing(c.id); };

  const save = async () => {
    if (!form.nama.trim()) return toast.error("Nama kategori wajib diisi");
    try {
      const payload = { nama: form.nama.trim(), deskripsi: form.deskripsi || "", urutan: Number(form.urutan || 0), aktif: !!form.aktif, tampil_publik: !!form.tampil_publik };
      if (editing === "new") await api.post("/categories", payload);
      else await api.put(`/categories/${editing}`, payload);
      toast.success("Kategori disimpan"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/categories/${confirmDel.id}`); toast.success("Kategori dihapus"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.patch("/categories/reorder", { ids: arr.map((c) => c.id) }); load(); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  return (
    <div className="animate-fade-up space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Tag size={26} weight="duotone" className="text-[#7C4DE0]" /><h1 className="font-heading text-3xl font-extrabold text-slate-800">Kategori Layanan</h1></div>
          <p className="mt-1 text-sm text-slate-500">{items.length} kategori · dipakai Master Layanan & booking publik{!canManage && " · hanya lihat"}</p>
        </div>
        {canManage && <button data-testid="add-category-button" onClick={openNew} className="flex items-center gap-2 rounded-xl bg-[#7C4DE0] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#6A38C7] active:scale-95"><Plus size={18} weight="bold" /> Tambah Kategori</button>}
      </div>

      <div className="space-y-2">
        {items.map((c, idx) => (
          <div key={c.id} data-testid={`category-${c.id}`} className="flex items-center gap-3 rounded-2xl border border-rose-100/70 bg-white p-3.5 shadow-sm">
            {canManage && (
              <div className="flex flex-col">
                <button data-testid={`cat-up-${c.id}`} onClick={() => move(idx, -1)} disabled={idx === 0} className="rounded p-0.5 text-slate-400 hover:text-[#6A38C7] disabled:opacity-30"><ArrowUp size={16} weight="bold" /></button>
                <button data-testid={`cat-down-${c.id}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="rounded p-0.5 text-slate-400 hover:text-[#6A38C7] disabled:opacity-30"><ArrowDown size={16} weight="bold" /></button>
              </div>
            )}
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EEE6FF] text-xs font-bold text-[#6A38C7]">{idx + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="font-heading font-bold text-slate-800">{c.nama}</p>
              {c.deskripsi && <p className="truncate text-xs text-slate-400">{c.deskripsi}</p>}
            </div>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.aktif ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{c.aktif ? "Aktif" : "Nonaktif"}</span>
            <span className={`hidden items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold sm:inline-flex ${c.tampil_publik ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-400"}`}>{c.tampil_publik ? <Eye size={12} /> : <EyeSlash size={12} />} {c.tampil_publik ? "Publik" : "Privat"}</span>
            {canManage && (
              <div className="flex gap-1.5">
                <button data-testid={`edit-category-${c.id}`} onClick={() => openEdit(c)} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-600"><PencilSimple size={14} /> Edit</button>
                {isSuper && <button data-testid={`delete-category-${c.id}`} onClick={() => setConfirmDel(c)} className="flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-600"><Trash size={14} /></button>}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="category-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "Tambah Kategori" : "Edit Kategori"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Nama Kategori</label><input data-testid="category-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} className={inputCls} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Deskripsi (opsional)</label><textarea data-testid="category-deskripsi" rows={2} value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} className={inputCls} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Urutan Tampil</label><input data-testid="category-urutan" type="number" value={form.urutan} onChange={(e) => setForm({ ...form, urutan: e.target.value })} className={inputCls} /></div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="category-aktif" type="checkbox" checked={!!form.aktif} onChange={(e) => setForm({ ...form, aktif: e.target.checked })} /> Aktif</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="category-publik" type="checkbox" checked={!!form.tampil_publik} onChange={(e) => setForm({ ...form, tampil_publik: e.target.checked })} /> Tampil di booking publik</label>
          </div>
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
            <button data-testid="save-category" onClick={save} className="rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white hover:bg-[#6A38C7]">Simpan</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="confirm-delete-category-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Hapus Kategori?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Yakin ingin menghapus kategori <b>{confirmDel?.nama}</b>? Kategori yang sudah dipakai layanan/reservasi tidak dapat dihapus — nonaktifkan saja.</p>
          <DialogFooter>
            <button onClick={() => setConfirmDel(null)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
            <button data-testid="confirm-delete-category" onClick={doDelete} className="rounded-xl bg-rose-500 px-5 py-2 text-sm font-bold text-white hover:bg-rose-600">Hapus</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
